import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { RouteQueryComposable as DispatchRouteQueryComposable } from '@/composables/routeQueries/useDispatchRouteQuery';
import type { DispatchDashboardViewWSComposable } from '@/composables/ws/useDispatchDashboardViewWS';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, toRaw, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';
import { format } from 'date-fns';
import _isEqual from 'lodash/isEqual';

import { useLoaderStore } from '@/stores/useLoaderStore';
import {
  fetchGetDespatchJobs,
  fetchGetJobLocation,
  // fetchPostJobCancel,
} from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

import colors from '@/styles/colors.module.scss';

export interface DispatchDashboardViewDataComposable {
  isFetchingDispatchJobs: Readonly<Ref<boolean>>;
  isFetchingStopLocations: Readonly<Ref<boolean>>;
  /** Fetching, or activated but not yet answered for. See the computed. */
  isAwaitingStopLocations: Readonly<Ref<boolean>>;

  dispatchJobs: Readonly<
    Ref<ReadonlyDeep<openapi.DespatchJobsContentOpenapi> | undefined>
  >;
  stopLocations: Readonly<
    Ref<ReadonlyDeep<openapi.JobLocationsContentOpenapi> | undefined>
  >;

  dispatchJobsLocks: Readonly<Ref<ReadonlyDeep<Record<number, boolean>>>>;
  dispatchDriversMarks: Readonly<Ref<ReadonlyDeep<Record<number, string>>>>;

  dispatchJobsError: Readonly<Ref<unknown | undefined>>;
  stopLocationsError: Readonly<Ref<unknown | undefined>>;

  refreshDispatchJobs: () => Promise<void>;
  refreshStopLocations: (params: {
    job_id: number;
    job_date: string;
  }) => Promise<void>;

  reload: () => DispatchDashboardViewDataComposable;
}

export default function useDispatchDashboardViewData(
  routeQuery: DispatchRouteQueryComposable,
  viewWS: DispatchDashboardViewWSComposable,
): DispatchDashboardViewDataComposable {
  const route = useRoute();

  const loaderStore = useLoaderStore();

  let dispatchJobsController: AbortController | undefined;
  let stopLocationsController: AbortController | undefined;

  const dispatchJobsFetchCounter = ref(0);
  const stopLocationsFetchCounter = ref(0);

  const isFetchingDispatchJobs = computed(
    () => !!dispatchJobsFetchCounter.value,
  );
  const isFetchingStopLocations = computed(
    () => !!stopLocationsFetchCounter.value,
  );

  const dispatchJobs = ref<ReadonlyDeep<openapi.DespatchJobsContentOpenapi>>();
  const stopLocations = ref<ReadonlyDeep<openapi.JobLocationsContentOpenapi>>();

  const dispatchJobsLocks = ref<Record<number, boolean>>({});
  // TODO in case of the locks they are initially included in jobs by default, but we don't get initialy all the marks
  const dispatchDriversMarks = ref<Record<number, string>>({});

  const dispatchJobsError = ref();
  const stopLocationsError = ref();

  // Activating a job clears the previous stops and only *then* schedules the
  // fetch, debounced by 2.5s - so `isFetchingStopLocations` is still false
  // while we are already waiting for an answer. `undefined` stops mean no
  // answer yet; an empty array is an answer, from a job with no stops.
  const isAwaitingStopLocations = computed(
    () =>
      isFetchingStopLocations.value ||
      (stopLocations.value === undefined && !stopLocationsError.value),
  );

  function clear(): void {
    if (dispatchJobsController) dispatchJobsController.abort();
    if (stopLocationsController) stopLocationsController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchDispatchJobs(): Promise<
    ReadonlyDeep<openapi.DespatchJobsContentOpenapi> | undefined
  > {
    if (dispatchJobsController) dispatchJobsController.abort();
    if (!routeQuery.date.value) return;
    dispatchJobsController = new AbortController();
    return await fetchGetDespatchJobs(
      {
        date: format(routeQuery.date.value, 'yyyy-MM-dd'),
      },
      {
        signal: dispatchJobsController.signal,
      },
    )
      .then(function (data) {
        dispatchJobsError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        dispatchJobsError.value = err;
        throw err;
      });
  }

  async function _fetchJobLocation(params: {
    job_id: number;
    job_date: string;
  }): Promise<ReadonlyDeep<openapi.JobLocationsContentOpenapi> | undefined> {
    if (stopLocationsController) stopLocationsController.abort();
    stopLocationsController = new AbortController();
    return await fetchGetJobLocation(params, {
      signal: stopLocationsController.signal,
    })
      .then((data) => {
        stopLocationsError.value = undefined;
        return data;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        stopLocationsError.value = err;
        throw err;
      });
  }

  async function refreshDispatchJobs(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    dispatchJobsFetchCounter.value++;
    try {
      dispatchJobs.value = await _fetchDispatchJobs();
    } finally {
      dispatchJobsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshStopLocations(params: {
    job_id: number;
    job_date: string;
  }): Promise<void> {
    loaderStore.increaseGlobalLoader();
    stopLocationsFetchCounter.value++;
    try {
      stopLocations.value = await _fetchJobLocation(params);
    } finally {
      stopLocationsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  // TODO what about `CancelCloseStopDialog`?
  // async function refreshCancelJob(): Promise<void> {
  //   loaderStore.increaseGlobalLoader();
  //   cancelJobFetchCounter.value++;
  //   dispatchJobs.value = await _fetchCancelJob().finally(function () {
  //     cancelJobFetchCounter.value--;
  //     loaderStore.decreaseGlobalLoader();
  //   });
  // }

  function reload(): DispatchDashboardViewDataComposable {
    void Promise.all([refreshDispatchJobs()]);
    return {
      isFetchingDispatchJobs,
      isFetchingStopLocations,
      isAwaitingStopLocations,
      dispatchJobs,
      stopLocations,
      dispatchJobsLocks,
      dispatchDriversMarks,
      dispatchJobsError,
      stopLocationsError,
      refreshDispatchJobs,
      refreshStopLocations,
      // refreshCancelJob,
      reload,
    };
  }

  async function jobWatchCb(
    dispatchJob:
      | ReadonlyDeep<openapi.DespatchJobsContentOpenapi[0]>
      | undefined,
  ) {
    if (!dispatchJobs.value) return;
    if (!dispatchJob) return;

    const _dispatchJobs = [...toRaw(dispatchJobs.value)];
    const _updatedDispatchJob = toRaw(dispatchJob);

    const updatedDispatchJobIndex = _dispatchJobs.findIndex(
      function (dispatchJob) {
        if (dispatchJob.id === _updatedDispatchJob.id) return true;
        return false;
      },
    );

    if (updatedDispatchJobIndex === -1) {
      dispatchJobs.value = [_updatedDispatchJob, ..._dispatchJobs];
    } else {
      _dispatchJobs[updatedDispatchJobIndex] = _updatedDispatchJob;
      dispatchJobs.value = _dispatchJobs;
    }
  }

  watch(viewWS.updatedDispatchJob, jobWatchCb);
  watch(viewWS.createdDispatchJob, jobWatchCb);

  watch(viewWS.lockedDispatchJobId, async function (newValues, oldValues) {
    if (isNaN(viewWS.lockedDispatchJobId.value)) return;
    dispatchJobsLocks.value[viewWS.lockedDispatchJobId.value] = true;
    dispatchJobsLocks.value = { ...dispatchJobsLocks.value };
  });
  watch(viewWS.unlockedDispatchJobId, async function (newValues, oldValues) {
    if (isNaN(viewWS.unlockedDispatchJobId.value)) return;
    dispatchJobsLocks.value[viewWS.unlockedDispatchJobId.value] = false;
    dispatchJobsLocks.value = { ...dispatchJobsLocks.value };
  });

  watch(
    viewWS.yellowMarkedDispatchDriverId,
    async function (newValues, oldValues) {
      if (isNaN(viewWS.yellowMarkedDispatchDriverId.value)) return;
      dispatchDriversMarks.value[viewWS.yellowMarkedDispatchDriverId.value] =
        colors.colorYellow500;
    },
  );
  watch(
    viewWS.redMarkedDispatchDriverId,
    async function (newValues, oldValues) {
      if (isNaN(viewWS.redMarkedDispatchDriverId.value)) return;
      dispatchDriversMarks.value[viewWS.redMarkedDispatchDriverId.value] =
        colors.colorRed700;
    },
  );
  watch(
    viewWS.greenMarkedDispatchDriverId,
    async function (newValues, oldValues) {
      if (isNaN(viewWS.greenMarkedDispatchDriverId.value)) return;
      dispatchDriversMarks.value[viewWS.greenMarkedDispatchDriverId.value] =
        colors.colorStatusSuccessIcon;
    },
  );
  watch(viewWS.unmarkedDispatchDriverId, async function (newValues, oldValues) {
    if (isNaN(viewWS.unmarkedDispatchDriverId.value)) return;
    dispatchDriversMarks.value[viewWS.unmarkedDispatchDriverId.value] =
      colors.colorGray200;
  });

  watch(
    [routeQuery.date],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.DISPATCH) return;
      if (_isEqual(newValues, oldValues)) return;
      reload();
    },
    { immediate: true },
  );

  return {
    isFetchingDispatchJobs,
    isFetchingStopLocations,
    isAwaitingStopLocations,
    dispatchJobs,
    stopLocations,
    dispatchJobsLocks,
    dispatchDriversMarks,
    dispatchJobsError,
    stopLocationsError,
    refreshDispatchJobs,
    refreshStopLocations,
    reload,
  };
}
